import {getTestProps} from '@benchling/js-test-utils';
import {Box, Button, CloseButton, createStyles, Group, Stack, Text, TextInput, Title} from '@mantine/core';
import React, {useCallback, useRef, useState} from 'react';

import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {
  AnimatedChoiceChip,
  CHIP_HEIGHT_PX,
  CHIP_RADIUS_PX,
  OnboardingProgressBar,
} from './OnboardingComponents';

/** Side length of the pencil icon badge, in pixels. */
const PENCIL_BADGE_SIZE_PX = 26;

const useFreeTextStyles = createStyles((theme, {selected}: {selected: boolean}) => ({
  closedButton: {
    fontWeight: 400,
    height: CHIP_HEIGHT_PX,
    borderRadius: CHIP_RADIUS_PX,
    border: `1px solid ${selected ? theme.colors.blue[1] : theme.colors.gray[2]}`,
    backgroundColor: selected ? theme.colors.blue[1] : theme.white,
    color: selected ? theme.colors.blue[8] : theme.colors.gray[6],
    '&:hover': {
      backgroundColor: theme.colors.blue[1],
      borderColor: theme.colors.blue[1],
      color: theme.colors.blue[8],
    },
  },
  openContainer: {
    display: 'inline-flex',
    alignItems: 'center',
    height: CHIP_HEIGHT_PX,
    border: `1px solid ${selected ? theme.colors.blue[1] : theme.colors.gray[2]}`,
    borderRadius: CHIP_RADIUS_PX,
    padding: `0 ${theme.spacing.md}`,
    cursor: 'text',
    backgroundColor: selected ? theme.colors.blue[1] : theme.white,
    color: selected ? theme.colors.blue[8] : theme.colors.gray[9],
  },
  // A lavender rounded-square badge holding the pencil glyph, matching the design mock.
  pencilBadge: {
    flex: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: PENCIL_BADGE_SIZE_PX,
    height: PENCIL_BADGE_SIZE_PX,
    marginRight: theme.spacing.xs,
    borderRadius: theme.radius.sm,
    backgroundColor: theme.colors.blue[0],
    color: theme.colors.blue[6],
    fontSize: theme.fontSizes.sm,
  },
  input: {
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: theme.fontSizes.sm,
    width: 160,
    padding: 0,
    color: 'inherit',
  },
}));

/**
 * A dashed "Something else" chip that expands into a free-text input. Shared by the onboarding agent
 * survey and the interest/first-chat screen so both present the identical control.
 */
export function FreeTextChoiceButton({
  value,
  onChange,
  onDiscard,
  testComponent,
}: {
  value: string;
  onChange: (value: string) => void;
  onDiscard: () => void;
  /** Component name used for test ids, so each screen keeps stable selectors. */
  testComponent: string;
}): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isSelected = value.trim().length > 0;
  const {classes} = useFreeTextStyles({selected: isSelected});

  const handleClick = useCallback(() => {
    setIsOpen(true);
    inputRef.current?.focus();
  }, []);

  const handleBlur = useCallback(() => {
    if (!value.trim()) {
      setIsOpen(false);
      onDiscard();
    }
  }, [value, onDiscard]);

  if (isOpen) {
    return (
      <Box
        className={classes.openContainer}
        onClick={() => inputRef.current?.focus()}
        {...getTestProps({component: testComponent, element: 'something-else-button'})}
      >
        <span className={classes.pencilBadge}>
          <BenchlingIcon icon="pen" aria-hidden={true} />
        </span>
        <TextInput
          size="md"
          ref={inputRef}
          autoFocus={true}
          variant="unstyled"
          classNames={{input: classes.input}}
          styles={{wrapper: {display: 'contents'}}}
          placeholder="Something else"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
          onBlur={handleBlur}
        />
      </Box>
    );
  }

  return (
    <Button
      aria-pressed={isSelected}
      variant="ghost"
      size="sm"
      className={classes.closedButton}
      leftIcon={
        <span className={classes.pencilBadge}>
          <BenchlingIcon icon="pen" aria-hidden={true} />
        </span>
      }
      onClick={handleClick}
      {...getTestProps({component: testComponent, element: 'something-else-button'})}
    >
      Something else
    </Button>
  );
}

const useShellStyles = createStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxWidth: 650 + 32,
    width: '100%',
    margin: '0 auto',
    padding: `${theme.spacing.xs} ${theme.spacing.md} ${theme.spacing.md}`,
  },
}));

/**
 * The outer chrome shared by every onboarding screen: a centered column with a persistent top-right
 * close (✕) button. The body fills the remaining height so children can distribute their content
 * vertically (e.g. centered question + bottom-pinned footer). Keeps the agent survey, the
 * interest/first-chat screen, and the intro screen visually identical.
 */
export function OnboardingScreenShell({
  testComponent,
  onExit,
  exitLabel = 'Exit onboarding',
  children,
}: {
  testComponent: string;
  onExit: () => void;
  exitLabel?: string;
  children: React.ReactNode;
}): JSX.Element {
  const {classes} = useShellStyles();
  return (
    <Box className={classes.container} {...getTestProps({component: testComponent, element: 'root'})}>
      <Group position="right" mb={4}>
        <CloseButton
          aria-label={exitLabel}
          size="sm"
          onClick={onExit}
          {...getTestProps({component: testComponent, element: 'exit-button'})}
        />
      </Group>
      {children}
    </Box>
  );
}

export interface OnboardingChoice {
  /** Choice label, also used as the React key. */
  label: string;
  selected: boolean;
  onSelect: () => void;
}

const useBodyStyles = createStyles((theme) => ({
  // Fills the shell and splits into a vertically-centered question block and a bottom-pinned footer,
  // matching the design's distributed layout.
  body: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  // The question + subtitle + choices, centered in the available vertical space above the footer.
  questionBlock: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  heading: {
    fontSize: theme.fontSizes.xl,
    fontWeight: 700,
    lineHeight: 1.3,
    textAlign: 'center',
  },
  subtitle: {
    textAlign: 'center',
  },
  choicesGrid: {
    flexWrap: 'wrap',
  },
  footer: {
    flex: 'none',
  },
}));

/**
 * The shared visual layout for an onboarding question: a centered heading and optional subtitle, a
 * centered wrapped grid of choice chips (optionally including a "Something else" free-text chip), an
 * optional progress bar, and a bottom-pinned footer (e.g. Skip).
 *
 * Both the agent survey ({@link AIOnboardingScreen}) and the interest/first-chat screen render this
 * so the final static question is visually indistinguishable from the questions preceding it. The
 * choice chips cascade in and replay whenever the question text changes.
 */
export function OnboardingQuestionView({
  question,
  subtitle,
  choices,
  freeText,
  footer,
  progressCount = 0,
  testComponent,
  element = 'choices',
}: {
  question: string;
  /** Supporting copy under the heading (e.g. "Improve AI responses by sharing details about yourself"). */
  subtitle?: string;
  choices: ReadonlyArray<OnboardingChoice>;
  /** When provided, renders the dashed "Something else" free-text chip after the choices. */
  freeText?: {value: string; onChange: (value: string) => void; onDiscard: () => void};
  /** Footer content pinned to the bottom (e.g. Skip). Omitted when null. */
  footer: React.ReactNode;
  /** Steps reached so far; renders one filled progress segment each above the footer. Omit/0 to hide. */
  progressCount?: number;
  /** Component name used for test ids. */
  testComponent: string;
  /** Test-id element name for the choices container (defaults to 'choices'). */
  element?: string;
}): JSX.Element {
  const {classes} = useBodyStyles();
  return (
    <Box className={classes.body}>
      <Stack className={classes.questionBlock} spacing="xl">
        <Stack spacing="sm" align="center">
          <Stack spacing="xs">
            <Title order={2} className={classes.heading}>
              {question}
            </Title>
            {subtitle != null && (
              <Text c="dimmed" className={classes.subtitle}>
                {subtitle}
              </Text>
            )}
          </Stack>
          {progressCount > 0 && <OnboardingProgressBar count={progressCount} testComponent={testComponent} />}
        </Stack>
        {/* Keyed by question so the chip cascade remounts and replays on each new question. */}
        <Group
          key={question}
          spacing="sm"
          position="center"
          className={classes.choicesGrid}
          {...getTestProps({component: testComponent, element})}
        >
          {choices.map((choice, index) => (
            <AnimatedChoiceChip
              key={choice.label}
              label={choice.label}
              selected={choice.selected}
              onSelect={choice.onSelect}
              index={index}
              animationKey={question}
            />
          ))}
          {freeText != null && (
            <FreeTextChoiceButton
              value={freeText.value}
              onChange={freeText.onChange}
              onDiscard={freeText.onDiscard}
              testComponent={testComponent}
            />
          )}
        </Group>
      </Stack>
      {footer != null && (
        <Stack align="center" spacing="xs" pt="md" className={classes.footer}>
          {footer}
        </Stack>
      )}
    </Box>
  );
}
